import Icon from '@ant-design/icons';
import { Button, Popconfirm, Table, Tooltip } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import connectionMonitorApi from '../../../../../api/ConnectionMonitorApi';
import { Page } from '../../../../../model/elements';
import { Connection, ConnectionMonitor } from '../../../../../model/entities';
import { ReactComponent as AddSvg } from '../../../../../resources/images/add.svg';
import { ReactComponent as TrashCanSvg } from '../../../../../resources/images/trash-can.svg';
import notificationService from '../../../../../services/NotificationService';
import tableService from '../../../../../services/TableService';
import ConnectionMonitoringModal from './ConnectionMonitoringModal/ConnectionMonitoringModal';

/**
 * Returns the connection monitoring component.
 * @props the props
 * @returns the connection monitoring component.
 */
const ConnectionMonitoringComponent = (props: Props): React.ReactElement => {
    const { connection } = props;
    /*** HOOKS ***/

    const intl = useIntl();
    const [connectionMonitorsPage, setConnectionMonitorsPage] = useState<Page<ConnectionMonitor>>();
    const [connectionMonitor, setConnectionMonitor] = useState<ConnectionMonitor>();
    const [connectionMonitorModalVisible, setConnectionMonitorModalVisible] = useState<boolean>();
    const [loading, setLoading] = useState<boolean>();
    const [deleting, setDeleting] = useState<number>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);

                await listConnectionMonitors(0, tableService.pageSize, 'name', true, connection.id!);
            } catch (error) {
                notificationService.displayError(error, intl);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [connection.id, intl]);

    /*** METHODS ***/

    const list = async () => {
        try {
            setLoading(true);

            const page = 0;
            const size = connectionMonitorsPage?.size || tableService.pageSize;
            const sortField = connectionMonitorsPage?.sort.field || 'name';
            const sortOrder = connectionMonitorsPage?.sort.order || true;
            await listConnectionMonitors(page, size, sortField, sortOrder, connection.id!);

            setConnectionMonitor(undefined);
            setConnectionMonitorModalVisible(undefined);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        try {
            setLoading(true);
            const page = pagination.current! - 1;
            const size = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            await listConnectionMonitors(page, size, sortField, sortOrder, connection.id!);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const listConnectionMonitors = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        connectionId: number,
    ) => {
        const connectionMonitorsPage = await connectionMonitorApi.list(page, size, sortField, sortOrder, connectionId);
        setConnectionMonitorsPage(connectionMonitorsPage);
    };

    const showConnectionMonitorModal = (
        connectionMonitorModalVisible: boolean,
        connectionMonitor?: ConnectionMonitor,
    ) => {
        setConnectionMonitor(connectionMonitor);
        setConnectionMonitorModalVisible(connectionMonitorModalVisible);
    };

    const remove = async (connectionMonitor: ConnectionMonitor) => {
        try {
            setDeleting(connectionMonitor.id);
            await connectionMonitorApi.delete(connectionMonitor, connection);
            await list();
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setDeleting(undefined);
        }
    };

    /*** COMPONENTS ***/

    const items = connectionMonitorsPage ? connectionMonitorsPage.content : [];
    const columns: ColumnsType<ConnectionMonitor> = [
        {
            title: <FormattedMessage id="connectionMonitor.name" />,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            defaultSortOrder: 'ascend',
            render: (value: string, connectionMonitor: ConnectionMonitor) => (
                <span className="link" onClick={() => showConnectionMonitorModal(true, connectionMonitor)}>
                    {value}
                </span>
            ),
        },
        {
            title: <FormattedMessage id="connectionMonitor.delay" />,
            dataIndex: 'delay',
            key: 'delay',
            sorter: true,
            align: 'right',
            width: 130,
            render: (value: number, connectionMonitor: ConnectionMonitor) => (
                <span className="link" onClick={() => showConnectionMonitorModal(true, connectionMonitor)}>
                    {value}
                </span>
            ),
        },
        {
            title: <FormattedMessage id="connectionMonitor.coolingOffPeriod" />,
            dataIndex: 'coolingOffPeriod',
            key: 'coolingOffPeriod',
            sorter: true,
            align: 'right',
            width: 210,
            render: (value: number, connectionMonitor: ConnectionMonitor) => (
                <span className="link" onClick={() => showConnectionMonitorModal(true, connectionMonitor)}>
                    {value}
                </span>
            ),
        },
        {
            title: <FormattedMessage id="connectionMonitor.project" />,
            dataIndex: 'project',
            key: 'project',
            sorter: true,
            render: (value: string, connectionMonitor: ConnectionMonitor) => (
                <span className="link" onClick={() => showConnectionMonitorModal(true, connectionMonitor)}>
                    {connectionMonitor.project?.name}
                </span>
            ),
        },
        {
            title: <FormattedMessage id="connectionMonitor.folder" />,
            dataIndex: 'folder',
            key: 'folder',
            sorter: true,
            render: (value: string, connectionMonitor: ConnectionMonitor) => (
                <span className="link" onClick={() => showConnectionMonitorModal(true, connectionMonitor)}>
                    {connectionMonitor.folder?.name}
                </span>
            ),
        },
        {
            dataIndex: 'id',
            key: 'action',
            align: 'center',
            width: '100px',
            render: (id: number, connectionMonitor: ConnectionMonitor) => (
                <Popconfirm
                    title={<FormattedMessage id="connectionMonitor.delete" />}
                    onConfirm={() => remove(connectionMonitor)}
                    okText={<FormattedMessage id="button.yes" />}
                    cancelText={<FormattedMessage id="button.no" />}
                    okButtonProps={{ loading: deleting === id }}
                    placement="left"
                >
                    <Button type="ghost" icon={<Icon component={TrashCanSvg} />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <>
            <div className={`toolbar`}>
                <Tooltip title={<FormattedMessage id="connectionMonitor.new.tooltip" />}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<Icon component={AddSvg} />}
                        onClick={() => showConnectionMonitorModal(true)}
                    >
                        <FormattedMessage id="connectionMonitor.button.addMonitor" tagName="span" />
                    </Button>
                </Tooltip>
            </div>
            <Table
                dataSource={items}
                columns={columns}
                onChange={paginate}
                pagination={tableService.createPagination(connectionMonitorsPage, {
                    position: ['bottomRight'],
                    pageSize: 10,
                })}
                rowKey="id"
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
            {connectionMonitorModalVisible && (
                <ConnectionMonitoringModal
                    connection={connection}
                    connectionMonitorId={connectionMonitor?.id}
                    visible={connectionMonitorModalVisible}
                    onSave={list}
                    onCancel={() => showConnectionMonitorModal(false)}
                />
            )}
        </>
    );
};

export default ConnectionMonitoringComponent;

interface Props {
    connection: Connection;
}
